import { useMemo } from 'react'
import { ErrorState } from '@shared/design-system/ui'
import { AppError } from '@shared/api/error'
import { useAdminReceivablesHourly } from '../api/admin.api'
import { adminListPeriodRange } from '../lib/admin-dashboard-period'
import { hourlyToSalesSeries } from '../lib/admin-hourly-receivables'
import { AdminSalesChart } from './AdminSalesChart'

function isMissingHourly(error: unknown) {
  return error instanceof AppError && (error.status === 404 || error.status === 400)
}

const DAY_MS = 86_400_000

export function AdminFinanceHourlyChart({
  currency,
  recipientId,
  periodLabel,
}: {
  currency: string
  recipientId?: string
  periodLabel: string
}) {
  const today = adminListPeriodRange('today')
  const yesterday = {
    dateFrom: new Date(Date.parse(today.dateFrom!) - DAY_MS).toISOString(),
    dateTo: today.dateFrom!,
  }
  const scope = recipientId ? { recipientId } : {}
  const todayQuery = useAdminReceivablesHourly({
    currency,
    dateFrom: today.dateFrom!,
    dateTo: today.dateTo!,
    ...scope,
  })
  const yesterdayQuery = useAdminReceivablesHourly({
    currency,
    dateFrom: yesterday.dateFrom,
    dateTo: yesterday.dateTo,
    ...scope,
  })
  const series = useMemo(
    () =>
      hourlyToSalesSeries(
        currency,
        today.dateFrom!,
        today.dateTo!,
        todayQuery.data?.items,
        yesterdayQuery.data?.items,
      ),
    [currency, today.dateFrom, today.dateTo, todayQuery.data?.items, yesterdayQuery.data?.items],
  )

  const todayFailed = todayQuery.isError && !isMissingHourly(todayQuery.error)
  const yesterdayFailed = yesterdayQuery.isError && !isMissingHourly(yesterdayQuery.error)
  if (todayFailed || yesterdayFailed) {
    return (
      <ErrorState
        title="Não foi possível carregar o gráfico de hoje"
        description="A série por hora não chegou. Tente novamente."
        onRetry={() => {
          void todayQuery.refetch()
          void yesterdayQuery.refetch()
        }}
      />
    )
  }

  return (
    <AdminSalesChart
      data={series}
      currency={currency}
      periodLabel={periodLabel}
      isLoading={todayQuery.isPending || yesterdayQuery.isPending}
      title="Recebíveis lançados hoje"
      caption="Processamento bruto do dia, com saldo, reserva, comissão e receita Vendepay"
      emptyLabel={`Nenhum recebível lançado hoje em ${currency}.`}
      unitLabel="lançamento"
    />
  )
}
